import { useEffect, useState } from 'react';
import { listMinistryInstruments } from '../../../api/instruments';
import type { TeamMember } from '../../../api/teams';
import type { Event } from '../../../types/event';
import type { Instrument } from '../../../types/instrument';
import { useEventStore } from '../../../stores/eventStore';

interface Props {
  teamId: string;
  ministryId: string;
  events: Event[];
  members: TeamMember[];
  onClose: () => void;
  onSaved: () => void;
}

export default function ManualAssignmentDialog({
  teamId,
  ministryId,
  events,
  members,
  onClose,
  onSaved,
}: Props) {
  const addAssignment = useEventStore((state) => state.addAssignment);
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [eventId, setEventId] = useState('');
  const [memberId, setMemberId] = useState('');
  const [instrumentId, setInstrumentId] = useState('');
  const [isLoadingInstruments, setIsLoadingInstruments] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scheduledEvents = events.filter((event) => event.status === 'SCHEDULED');
  const activeInstruments = instruments.filter((instrument) => instrument.is_active);

  useEffect(() => {
    let isMounted = true;
    setIsLoadingInstruments(true);
    listMinistryInstruments(ministryId)
      .then((result) => {
        if (isMounted) setInstruments(result);
      })
      .catch(() => {
        if (isMounted) setError('Não foi possível carregar os instrumentos da equipe.');
      })
      .finally(() => {
        if (isMounted) setIsLoadingInstruments(false);
      });

    return () => {
      isMounted = false;
    };
  }, [ministryId]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSaving(true);
    try {
      await addAssignment(teamId, eventId, memberId, instrumentId);
      onSaved();
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } }).response?.status;
      setError(
        status === 403
          ? 'Apenas administradores e líderes da equipe podem criar atribuições na escala.'
          : 'Não foi possível criar a atribuição. Tente novamente.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  const selectClassName = 'w-full rounded-lg border border-white/10 bg-[#0A0A0F] px-3 py-2.5 text-sm text-white outline-none focus:border-[#C8A96E]';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="manual-assignment-title"
        className="w-full max-w-lg rounded-xl border border-white/10 bg-[#111118] p-6"
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 id="manual-assignment-title" className="text-lg font-semibold text-white">
            Nova atribuição
          </h2>
          <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            className="text-white/40 transition-colors hover:text-white"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {error && (
          <p role="alert" className="mb-4 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-300">
            {error}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block space-y-1.5 text-sm text-white/60">
            Evento
            <select
              required
              value={eventId}
              onChange={(event) => setEventId(event.target.value)}
              className={selectClassName}
            >
              <option value="">Selecione um evento</option>
              {scheduledEvents.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name} · {new Date(`${event.date}T12:00:00`).toLocaleDateString('pt-BR')}
                </option>
              ))}
            </select>
          </label>

          <label className="block space-y-1.5 text-sm text-white/60">
            Membro da equipe
            <select
              required
              value={memberId}
              onChange={(event) => setMemberId(event.target.value)}
              className={selectClassName}
            >
              <option value="">Selecione um membro</option>
              {members.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.user_name}
                </option>
              ))}
            </select>
          </label>

          <label className="block space-y-1.5 text-sm text-white/60">
            Instrumento ou voz
            <select
              required
              value={instrumentId}
              onChange={(event) => setInstrumentId(event.target.value)}
              disabled={isLoadingInstruments}
              className={selectClassName}
            >
              <option value="">
                {isLoadingInstruments ? 'Carregando instrumentos...' : 'Selecione um instrumento'}
              </option>
              {activeInstruments.map((instrument) => (
                <option key={instrument.id} value={instrument.id}>
                  {instrument.name}
                </option>
              ))}
            </select>
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/60 hover:text-white"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving || isLoadingInstruments || scheduledEvents.length === 0 || members.length === 0 || activeInstruments.length === 0}
              className="rounded-lg bg-[#C8A96E] px-4 py-2 text-sm font-semibold text-[#0A0A0F] transition-colors hover:bg-[#D4B87A] disabled:opacity-50"
            >
              {isSaving ? 'Salvando...' : 'Atribuir membro'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}